import { Component, effect, inject, signal, untracked } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';

import { LoopAllocationSettings } from '@empathy/survey/base';

import { loopSettingsIntegerValidator } from './loop-settings-integer-validator.function';

import { IBlockForm } from '../../../models/blocks/block-form.interface';
import { SurveyEditorService } from '../../../services/survey-editor.service';

@Component({
  selector: 'empathy-loop-settings',
  templateUrl: 'loop-settings.component.html',
  styleUrls: ['./loop-settings.component.scss']
})
export class LoopSettingsComponent {
  readonly configuredCount$ = signal(0);
  readonly quantityPlaceholder$ = signal('All 0');
  readonly faXmark = faXmark;
  readonly settingsForm = new FormGroup({
    randomized: new FormControl(false),
    quantity: new FormControl<number | string>('', [
      loopSettingsIntegerValidator,
      // Count changes revalidate this control without rerunning the selection effect and resetting input.
      (control: AbstractControl): ValidationErrors | null => Validators.max(untracked(this.configuredCount$))(control)
    ]),
    completionTarget: new FormControl<number | string>('', loopSettingsIntegerValidator)
  });

  private readonly editorService = inject(SurveyEditorService);

  constructor() {
    effect(
      (onCleanup) => {
        const selection = this.editorService.loopSettingsSelection$();
        if (!selection) {
          return;
        }
        const controls = selection.block.controls;
        this.updateConfiguredCount(selection.block);
        const subscription = selection.block.valueChanges.subscribe(() => this.updateConfiguredCount(selection.block));
        this.settingsForm.reset(
          {
            randomized: controls.sourceOptionsRandomizedInd.value,
            quantity: controls.allocation.value?.quantity ?? '',
            completionTarget: controls.allocation.value?.completionTarget ?? ''
          },
          { emitEvent: false }
        );
        subscription.add(this.settingsForm.valueChanges.subscribe(() => this.applyChanges(selection.block)));
        onCleanup(() => subscription.unsubscribe());
        this.updateValidationError();
      },
      { allowSignalWrites: true }
    );
  }

  onClose(): void {
    const trigger = this.editorService.loopSettingsSelection$()?.trigger;
    this.editorService.closeLoopSettings();
    trigger?.focus();
  }

  private applyChanges(block: FormGroup<IBlockForm>): void {
    for (const field of ['quantity', 'completionTarget'] as const) {
      const control = this.settingsForm.controls[field];
      if (Number(control.value) < 0) {
        control.setValue(null, { emitEvent: false });
      }
    }
    if (this.updateValidationError()) {
      return;
    }
    const allocation: LoopAllocationSettings = {};
    for (const field of ['quantity', 'completionTarget'] as const) {
      const control = this.settingsForm.controls[field];
      const value = control.value;
      if (value !== '' && value !== null && value !== undefined) {
        allocation[field] = Number(value);
      }
    }
    block.patchValue({
      allocation: Object.keys(allocation).length ? allocation : undefined,
      sourceOptionsRandomizedInd: this.settingsForm.controls.randomized.value
    });
    this.editorService.surveyEditor$().blocks.refreshLists();
  }

  private updateValidationError(): boolean {
    for (const field of ['quantity', 'completionTarget'] as const) {
      const control = this.settingsForm.controls[field];
      if (!control.invalid) {
        continue;
      }
      control.markAsTouched();
      return true;
    }
    return false;
  }

  private updateConfiguredCount(block: FormGroup<IBlockForm>): void {
    const controls = block.controls;
    const count = (controls.sourceMedia.value ?? []).length;
    this.configuredCount$.set(count);
    this.quantityPlaceholder$.set(`All ${count}`);
    this.settingsForm.controls.quantity.updateValueAndValidity({ emitEvent: false });
    this.updateValidationError();
  }
}
